import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router";

import smoothActiveScrollToTop from "../../utils/smoothActiveScrollToTop.js";

// How long after a route change to re-check the scroll-to-top (see ScrollToTop).
const ROUTE_SCROLL_SETTLE_MS = 300;
// How long a back/forward restore keeps retrying while the page grows tall enough.
const RESTORE_TIMEOUT_MS = 1500;

// Last window scroll offset per history entry (location.key).
const savedPositions = new Map();

const pageScrollY = () =>
  window.scrollY || document.documentElement?.scrollTop || 0;

// Back/forward: return to where the reader was. Content often mounts over the
// next frames, so keep re-applying until it sticks, times out, or they scroll.
const restoreScroll = (y) => {
  let userScrolled = false;
  const onUserScroll = () => {
    userScrolled = true;
  };
  globalThis.addEventListener("touchstart", onUserScroll, { passive: true });
  globalThis.addEventListener("wheel", onUserScroll, { passive: true });

  const startedAt = Date.now();
  let frame;
  const apply = () => {
    if (userScrolled) {
      return;
    }
    globalThis.scrollTo(0, y);
    const reached = Math.abs(pageScrollY() - y) < 2;
    if (!reached && Date.now() - startedAt < RESTORE_TIMEOUT_MS) {
      frame = globalThis.requestAnimationFrame(apply);
    }
  };
  apply();

  return () => {
    globalThis.cancelAnimationFrame(frame);
    globalThis.removeEventListener("touchstart", onUserScroll);
    globalThis.removeEventListener("wheel", onUserScroll);
  };
};

// Scroll to top only when the location pathname actually changes; back/forward
// restores the previous position instead.
// Defined outside App so the component identity is stable and it won't remount on every App re-render.
export default function ScrollToTop() {
  const { pathname, search, hash, state, key } = useLocation();
  const navigationType = useNavigationType();
  const preserveScroll = Boolean(state?.preserveScroll);
  const prevRouteRef = useRef();
  const activeKeyRef = useRef(key);

  // Set during commit, before the browser clamps the old page's offset, so a
  // late scroll event can't overwrite the entry being left.
  useLayoutEffect(() => {
    activeKeyRef.current = key;
  }, [key]);

  useEffect(() => {
    const record = () => {
      if (activeKeyRef.current === key) {
        savedPositions.set(key, pageScrollY());
      }
    };
    window.addEventListener("scroll", record, { passive: true });
    return () => window.removeEventListener("scroll", record);
  }, [key]);

  useEffect(() => {
    const nextRoute = `${pathname}${search}`;

    if (prevRouteRef.current !== nextRoute) {
      const isFirstRender = prevRouteRef.current === undefined;
      prevRouteRef.current = nextRoute;
      if (
        !isFirstRender &&
        navigationType === "POP" &&
        savedPositions.has(key)
      ) {
        return restoreScroll(savedPositions.get(key));
      }
      // A hash means the destination owns the scroll position (see
      // useDynamicPageHashScroll) — scrolling to top here would fight it.
      if (hash) return;
      // In-place URL updates (e.g. Browse keyword search) opt out via
      // navigation state so typing doesn't yank the page to the top.
      if (preserveScroll) return;
      // Scroll the active container because this app can use nested scrollers.
      // Instant, not smooth: iOS Safari aborts a smooth programmatic scroll
      // part-way when the outgoing page's layout changes under it (or while a
      // momentum fling is still running), leaving the new page stuck mid-way.
      smoothActiveScrollToTop({ smooth: false });
      // The new page often mounts/grows over the next frames (lazy routes,
      // skeleton -> content), which can nudge the offset again — re-assert the
      // top once it settles, unless the user has started scrolling themselves.
      let userScrolled = false;
      const onUserScroll = () => {
        userScrolled = true;
      };
      const reassert = () => {
        if (!userScrolled) smoothActiveScrollToTop({ smooth: false });
      };
      window.addEventListener("touchstart", onUserScroll, { passive: true });
      window.addEventListener("wheel", onUserScroll, { passive: true });
      const frame = window.requestAnimationFrame(reassert);
      const timer = setTimeout(reassert, ROUTE_SCROLL_SETTLE_MS);
      return () => {
        window.cancelAnimationFrame(frame);
        clearTimeout(timer);
        window.removeEventListener("touchstart", onUserScroll);
        window.removeEventListener("wheel", onUserScroll);
      };
    }
    return undefined;
  }, [pathname, search, hash, preserveScroll, navigationType, key]);
  return null; // no UI
}
